export interface TouchControlCallbacks {
  onMove(forward: number, right: number): void;
  onLook(deltaX: number, deltaY: number): void;
  onJump(): void;
}

type Direction = "forward" | "back" | "left" | "right";

const directionVector: Record<Direction, readonly [number, number]> = {
  forward: [1, 0],
  back: [-1, 0],
  left: [0, -1],
  right: [0, 1],
};

export class TouchControls {
  private readonly movementPointers = new Map<number, Direction>();
  private lookPointer: number | undefined;
  private lookX = 0;
  private lookY = 0;

  constructor(
    private readonly root: HTMLElement,
    private readonly callbacks: TouchControlCallbacks,
  ) {
    for (const button of root.querySelectorAll<HTMLButtonElement>(
      "[data-touch-direction]",
    )) {
      const start = (event: PointerEvent) => {
        if (event.pointerType === "mouse" && event.button !== 0) return;
        event.preventDefault();
        const direction = button.dataset.touchDirection as Direction;
        this.movementPointers.set(event.pointerId, direction);
        button.classList.add("touch-button--pressed");
        this.reportMovement();
        try {
          button.setPointerCapture?.(event.pointerId);
        } catch {
          // Synthetic automation events are not registered as active pointers.
        }
      };
      const stop = (event: PointerEvent) => {
        if (!this.movementPointers.delete(event.pointerId)) return;
        event.preventDefault();
        button.classList.remove("touch-button--pressed");
        if (button.hasPointerCapture?.(event.pointerId))
          button.releasePointerCapture(event.pointerId);
        this.reportMovement();
      };
      button.addEventListener("pointerdown", start);
      button.addEventListener("pointerup", stop);
      button.addEventListener("pointercancel", stop);
      button.addEventListener("lostpointercapture", stop);
      button.addEventListener("contextmenu", (event) => event.preventDefault());
    }

    const look = this.requiredElement<HTMLElement>("[data-touch-look]");
    look.addEventListener("pointerdown", (event) => {
      if (this.lookPointer !== undefined) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      event.preventDefault();
      this.lookPointer = event.pointerId;
      this.lookX = event.clientX;
      this.lookY = event.clientY;
      look.classList.add("touch-look--pressed");
      try {
        look.setPointerCapture?.(event.pointerId);
      } catch {
        // Synthetic automation events are not registered as active pointers.
      }
    });
    look.addEventListener("pointermove", (event) => {
      if (event.pointerId !== this.lookPointer) return;
      event.preventDefault();
      const deltaX = event.clientX - this.lookX;
      const deltaY = event.clientY - this.lookY;
      this.lookX = event.clientX;
      this.lookY = event.clientY;
      this.callbacks.onLook(deltaX, deltaY);
    });
    const stopLook = (event: PointerEvent) => {
      if (event.pointerId !== this.lookPointer) return;
      event.preventDefault();
      this.lookPointer = undefined;
      look.classList.remove("touch-look--pressed");
      if (look.hasPointerCapture?.(event.pointerId))
        look.releasePointerCapture(event.pointerId);
    };
    look.addEventListener("pointerup", stopLook);
    look.addEventListener("pointercancel", stopLook);
    look.addEventListener("lostpointercapture", stopLook);
    look.addEventListener("contextmenu", (event) => event.preventDefault());

    this.requiredElement<HTMLButtonElement>(
      "[data-touch-jump]",
    ).addEventListener("click", () => this.callbacks.onJump());
    window.addEventListener("blur", this.clear);
    window.addEventListener("resize", this.clear);
    window.addEventListener("orientationchange", this.clear);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  }

  readonly clear = (): void => {
    this.movementPointers.clear();
    this.lookPointer = undefined;
    for (const pressed of this.root.querySelectorAll(
      ".touch-button--pressed, .touch-look--pressed",
    )) {
      pressed.classList.remove("touch-button--pressed", "touch-look--pressed");
    }
    this.callbacks.onMove(0, 0);
  };

  private readonly onVisibilityChange = (): void => {
    this.clear();
  };

  private reportMovement(): void {
    let forward = 0;
    let right = 0;
    for (const direction of this.movementPointers.values()) {
      const [nextForward, nextRight] = directionVector[direction];
      forward += nextForward;
      right += nextRight;
    }
    this.callbacks.onMove(
      Math.max(-1, Math.min(1, forward)),
      Math.max(-1, Math.min(1, right)),
    );
  }

  private requiredElement<T extends HTMLElement>(selector: string): T {
    const element = this.root.querySelector<T>(selector);
    if (!element)
      throw new Error(`Required touch control not found: ${selector}`);
    return element;
  }
}
